import { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Linking } from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import HuggingFaceIcon from './icons/HuggingFaceIcon';
import { Link, type Href } from 'expo-router';
import colors from '../lib/colors';
import { isPaperInboxLive } from '../lib/paperInbox';
import { useSession } from '../lib/SessionContext';
import { shouldShowPaperForwardNavLink } from '../lib/forwardAccess';
import { responsive } from '../lib/responsive';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import { useLingui } from '@lingui/react';
import { boundarySafeHref } from '../lib/i18n/publicWeb';
import { useAppLocale } from '../lib/i18n/AppI18nProvider';
import { DOCUMENTATION_ORIGIN, documentationHref } from '../lib/i18n/documentation';

type FooterLink = { href: Href; label: MessageDescriptor };

const BASE_PRODUCT_LINKS = [
    { href: '/product', label: msg({ id: 'footer.link.product', message: 'Product Overview' }) },
    { href: '/agents', label: msg({ id: 'footer.link.agents', message: 'AI Agents' }) },
    { href: '/research-briefs', label: msg({ id: 'footer.link.researchBriefs', message: 'Research Briefs' }) },
    { href: '/features', label: msg({ id: 'footer.link.features', message: 'All Features' }) },
    { href: '/pricing', label: msg({ id: 'footer.link.pricing', message: 'Pricing' }) },
    { href: DOCUMENTATION_ORIGIN, label: msg({ id: 'footer.link.docs', message: 'Documentation' }) },
] as FooterLink[];

const RESOURCES_LINKS = [
    { href: '/public-digests', label: msg({ id: 'footer.link.publicDigests', message: 'Public Digests' }) },
    { href: '/news', label: msg({ id: 'footer.link.news', message: 'News' }) },
    { href: '/newsletter', label: msg({ id: 'footer.link.newsletter', message: 'Newsletter' }) },
    { href: '/summarize', label: msg({ id: 'footer.link.paperSummary', message: 'Paper Summary' }) },
] as FooterLink[];

const COMPANY_LINKS = [
    { href: '/about', label: msg({ id: 'footer.link.about', message: 'About' }) },
    { href: '/about#press', label: msg({ id: 'footer.link.press', message: 'Press' }) },
    { href: '/about#contact', label: msg({ id: 'footer.link.contact', message: 'Contact' }) },
] as FooterLink[];

const LEGAL_LINKS = [
    { href: '/terms', label: msg({ id: 'footer.link.terms', message: 'Terms' }) },
    { href: '/privacy', label: msg({ id: 'footer.link.privacy', message: 'Privacy' }) },
] as FooterLink[];

const SOCIAL_ICON_SIZE = 22;

export default function Footer() {
    const currentYear = new Date().getFullYear();
    const { session } = useSession();
    const { i18n } = useLingui();
    const { messageLocale } = useAppLocale();
    const isForwardLinkPublic = isPaperInboxLive();
    const shouldShowForwardLink = shouldShowPaperForwardNavLink({
        session,
        isPublicLive: isForwardLinkPublic,
    });
    const productLinks = useMemo(() => {
        if (!shouldShowForwardLink) {
            return BASE_PRODUCT_LINKS;
        }

        return [
            BASE_PRODUCT_LINKS[0],
            {
                href: '/forward',
                label: msg({ id: 'footer.link.paperForward', message: 'Paper Forward' }),
            } as FooterLink,
            ...BASE_PRODUCT_LINKS.slice(1),
        ];
    }, [shouldShowForwardLink]);

    const handleTwitterPress = () => {
        Linking.openURL('https://twitter.com/paperzilla_ai');
    };

    const handleLinkedInPress = () => {
        Linking.openURL('https://www.linkedin.com/company/paperzilla');
    };

    const handleGitHubPress = () => {
        Linking.openURL('https://github.com/paperzilla-ai');
    };

    const handleHuggingFacePress = () => {
        Linking.openURL('https://huggingface.co/paperzilla');
    };

    const renderLinkColumn = (heading: MessageDescriptor, links: FooterLink[]) => (
        <View style={styles.linkColumn} {...responsive('footer-link-column')}>
            <Text style={styles.columnHeading} {...responsive('footer-column-heading')}>
                {i18n._(heading)}
            </Text>
            {links.map((item) => (
                <Link
                    key={item.href.toString()}
                    href={item.href === DOCUMENTATION_ORIGIN
                        ? documentationHref(messageLocale) : boundarySafeHref(item.href)}
                    style={styles.navLink}
                    {...responsive('footer-nav-link')}
                >
                    {i18n._(item.label)}
                </Link>
            ))}
        </View>
    );

    return (
        <View style={styles.footer}>
            <View style={styles.container} {...responsive('footer-container')}>
                <View style={styles.topSection} {...responsive('footer-top-row')}>
                    <View style={styles.brandColumn} {...responsive('footer-brand')}>
                        <Text style={styles.brandName} {...responsive('footer-brand-title')}><Trans id="footer.paperzilla" comment="UI copy in components/Footer.tsx">
                            Paperzilla
                        </Trans></Text>
                        <View style={styles.socialIcons} {...responsive('footer-social-icons')}>
                            <TouchableOpacity
                                onPress={handleGitHubPress}
                                style={styles.socialIcon}
                                {...responsive('footer-social-icon')}
                            >
                                <FontAwesome6 name="github" size={SOCIAL_ICON_SIZE} color={colors.mediumGray} />
                            </TouchableOpacity>
                            <TouchableOpacity
                                onPress={handleHuggingFacePress}
                                style={styles.socialIcon}
                                {...responsive('footer-social-icon')}
                            >
                                <HuggingFaceIcon size={SOCIAL_ICON_SIZE} color={colors.mediumGray} />
                            </TouchableOpacity>
                            <TouchableOpacity
                                onPress={handleLinkedInPress}
                                style={styles.socialIcon}
                                {...responsive('footer-social-icon')}
                            >
                                <FontAwesome6 name="linkedin" size={SOCIAL_ICON_SIZE} color={colors.mediumGray} />
                            </TouchableOpacity>
                            <TouchableOpacity
                                onPress={handleTwitterPress}
                                style={styles.socialIcon}
                                {...responsive('footer-social-icon')}
                            >
                                <FontAwesome6 name="square-x-twitter" size={SOCIAL_ICON_SIZE} color={colors.mediumGray} />
                            </TouchableOpacity>
                        </View>
                    </View>

                    <View style={styles.linkColumns} {...responsive('footer-center-links')}>
                        {renderLinkColumn(msg({ id: 'footer.column.product', message: 'Product' }), productLinks)}
                        {renderLinkColumn(msg({ id: 'footer.column.resources', message: 'Resources' }), RESOURCES_LINKS)}
                        {renderLinkColumn(msg({ id: 'footer.column.company', message: 'Company' }), COMPANY_LINKS)}
                        {renderLinkColumn(msg({ id: 'footer.column.legal', message: 'Legal' }), LEGAL_LINKS)}
                    </View>
                </View>

                <View style={styles.separator} {...responsive('footer-separator')} />

                <View style={styles.bottomRow} {...responsive('footer-bottom-row')}>
                    <Text style={styles.copyright} {...responsive('footer-copyright')}><Trans id="footer.paperzillaAllRightsReserved" comment="UI copy in components/Footer.tsx">
                        ©{currentYear} Paperzilla All Rights Reserved.
                    </Trans></Text>
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    footer: {
        borderTopWidth: 1,
        borderTopColor: '#e5e7eb',
        backgroundColor: colors.cookieBarGray,
        width: '100%',
    },
    container: {
        paddingTop: 24,
        paddingBottom: 24,
        paddingHorizontal: 24,
        maxWidth: 1200,
        alignSelf: 'center',
        width: '100%',
    },
    topSection: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 32,
        paddingBottom: 18,
    },
    brandColumn: {
        flex: 1,
        minWidth: 220,
        gap: 14,
    },
    linkColumns: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
        width: 820,
    },
    linkColumn: {
        width: 144,
        minWidth: 144,
        gap: 8,
    },
    separator: {
        height: 1,
        backgroundColor: colors.mediumGray,
        marginVertical: 0,
    },
    brandName: {
        fontSize: 28,
        fontWeight: 'bold',
        color: colors.mediumGray,
    },
    columnHeading: {
        fontSize: 13,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        color: '#d1d5db',
        marginBottom: 6,
    },
    navLink: {
        fontSize: 15,
        lineHeight: 22,
        color: colors.mediumGray,
        paddingVertical: 3,
    },
    socialIcons: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginTop: 2,
    },
    socialIcon: {
        paddingVertical: 6,
        paddingRight: 8,
    },
    bottomRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        paddingTop: 16,
    },
    copyright: {
        fontSize: 12,
        lineHeight: 18,
        color: colors.mediumGray,
    },
});
